import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanText, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/input';
import { toast } from 'sonner';

/**
 * OCR de facturas: encender o apagar la lectura por imagen.
 *
 * Solo entra en juego cuando el texto del PDF no trae el nombre del proveedor o su CIF: se rasteriza la
 * primera página y se lee el CIF. El nombre no se toma del lector, porque sale deformado.
 */

interface MotorOcr {
  id: string;
  nombre: string;
  disponible: boolean;
  motivo?: string;
}

interface EstadoOcr {
  enabled: boolean;
  active: boolean;
  available: boolean;
  defaultFromEnv: boolean;
  engine: string;
  engines: MotorOcr[];
}

export function OcrPanel() {
  const qc = useQueryClient();
  const estado = useQuery({
    queryKey: ['admin', 'ocr'],
    queryFn: () => api<{ data: EstadoOcr }>('/admin/ocr')
  });

  const cambiarMotor = async (motor: string) => {
    try {
      await api('/admin/ocr', { method: 'PUT', body: { enabled: datos?.enabled ?? false, engine: motor } });
      await qc.invalidateQueries({ queryKey: ['admin', 'ocr'] });
      toast.success('Motor de lectura cambiado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se ha podido guardar');
    }
  };

  const cambiar = async (encender: boolean) => {
    try {
      await api('/admin/ocr', { method: 'PUT', body: { enabled: encender } });
      await qc.invalidateQueries({ queryKey: ['admin', 'ocr'] });
      toast.success(encender ? 'OCR activado' : 'OCR desactivado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se ha podido guardar');
    }
  };

  const datos = estado.data?.data;
  const encendido = Boolean(datos?.enabled);
  const sinModelo = datos ? !datos.available : false;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <ScanText className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div>
            <h3 className="font-medium">Lectura de facturas por imagen (OCR)</h3>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Cuando el texto del PDF no trae el nombre del proveedor o su CIF, se lee la primera página
              como imagen. Solo se toma el CIF, y únicamente si pasa el dígito de control. El nombre, si no
              está en el texto, se pide en la revisión: el lector lo devuelve deformado y no se usa.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              La lectura es local (no sale ningún dato de la empresa) y cuesta unos 4 segundos por factura,
              solo cuando hace falta.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge tone={datos?.active ? 'success' : 'neutral'}>{datos?.active ? 'Activo' : 'Apagado'}</Badge>
          <Button
            variant={encendido ? 'outline' : 'default'}
            disabled={estado.isLoading || !datos || (sinModelo && !encendido)}
            onClick={() => void cambiar(!encendido)}
          >
            {encendido ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      </div>

      <div className="mt-4 max-w-sm">
        <Field label="Motor de lectura">
          <Select
            value={datos?.engine ?? 'tesseract'}
            onChange={(evento) => void cambiarMotor(evento.target.value)}
            disabled={estado.isLoading}
          >
            {(datos?.engines ?? [{ id: 'tesseract', nombre: 'Tesseract (local)', disponible: true }]).map((motor) => (
              <option key={motor.id} value={motor.id} disabled={!motor.disponible}>
                {motor.nombre}{motor.disponible ? '' : ' — no disponible en este servidor'}
              </option>
            ))}
          </Select>
        </Field>
        {datos?.engines?.find((motor) => motor.id === datos.engine && !motor.disponible)?.motivo ? (
          <p className="mt-1 text-sm text-muted-foreground">
            {datos.engines.find((motor) => motor.id === datos.engine)?.motivo}
          </p>
        ) : null}
      </div>

      {sinModelo && !encendido ? (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <ShieldAlert className="h-4 w-4" />
          El modelo de lectura no está instalado en este servidor: aunque se active, la importación no lo usará.
        </p>
      ) : null}
      {datos?.enabled && sinModelo ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Está activado, pero ahora mismo no tiene efecto porque falta el modelo. Puedes desactivarlo aquí.
        </p>
      ) : null}
    </Card>
  );
}
